import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import * as assetService from "../../services/assetService";
import { AssetItem, AssetCreatePayload, AssetUpdatePayload } from "../../services/assetService";

interface AssetState {
  items: AssetItem[];
  selectedAsset: AssetItem | null;
  loading: boolean;
  error: string | null;
}

const initialState: AssetState = {
  items: [],
  selectedAsset: null,
  loading: false,
  error: null,
};

export const fetchAssets = createAsyncThunk(
  "assets/fetchAssets",
  async (
    { tenantId, filters }: { tenantId: string; filters?: { assetClass?: number; status?: number } }
  ) => {
    const items = await assetService.getAssetsByTenant(tenantId, filters);
    return items;
  }
);

export const createAsset = createAsyncThunk(
  "assets/createAsset",
  async (data: AssetCreatePayload) => {
    const response = await assetService.createAsset(data);
    return response.result;
  }
);

export const updateAsset = createAsyncThunk(
  "assets/updateAsset",
  async ({ id, data }: { id: string; data: AssetUpdatePayload }) => {
    const response = await assetService.updateAsset(id, data);
    return id;
  }
);

export const deleteAsset = createAsyncThunk(
  "assets/deleteAsset",
  async (id: string) => {
    await assetService.deleteAsset(id);
    return id;
  }
);

const assetSlice = createSlice({
  name: "Assets",
  initialState,
  reducers: {
    selectAsset(state, action: PayloadAction<string>) {
      state.selectedAsset =
        state.items.find((a) => a.id === action.payload) || null;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAssets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAssets.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload || [];
      })
      .addCase(fetchAssets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch assets";
      })
      .addCase(createAsset.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAsset.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.items = state.items.filter((a) => a.id !== action.payload);
        }
      })
      .addCase(createAsset.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to create asset";
      })
      .addCase(updateAsset.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAsset.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateAsset.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update asset";
      })
      .addCase(deleteAsset.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAsset.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter((a) => a.id !== action.payload);
      })
      .addCase(deleteAsset.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to delete asset";
      });
  },
});

export const { selectAsset, clearError } = assetSlice.actions;
export default assetSlice.reducer;

export const selectAssetList = (state: any) => state.Assets?.items || [];
export const selectSelectedAsset = (state: any) => state.Assets?.selectedAsset || null;
export const selectAssetById = (state: any, id: string) =>
  state.Assets?.items?.find((a: AssetItem) => a.id === id) || null;
export const selectAssetLoading = (state: any) => state.Assets?.loading || false;
export const selectAssetError = (state: any) => state.Assets?.error || null;
